import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from '@/utils/cx';
import styles from '@/styles/components/Tabs/style.scss';

export interface TabOption<T extends string> {
  value: T;
  label: string;
}

interface TabsProps<T extends string> {
  /** Accessible name of the tab list, e.g. "Balance bills". */
  label: string;
  options: TabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Shown on the right of the tab row (e.g. a day picker for the current tab). */
  aside?: ReactNode;
}

/**
 * Tabs that switch a page between views (Owed / Recovered). Underlined text tabs on a hairline, so they don't look
 * like the filter buttons (SegmentedControl), which narrow a list. Arrow keys, Home and End move between tabs.
 */
export function Tabs<T extends string>({ label, options, value, onChange, aside }: TabsProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);

  const onKeyDown = (e: KeyboardEvent) => {
    const i = options.findIndex((o) => o.value === value);
    const next =
      e.key === 'ArrowRight' ? (i + 1) % options.length
      : e.key === 'ArrowLeft' ? (i - 1 + options.length) % options.length
      : e.key === 'Home' ? 0
      : e.key === 'End' ? options.length - 1
      : -1;
    if (next < 0) return;
    e.preventDefault();
    onChange(options[next]!.value);
    listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  };

  return (
    <div className={styles.bar}>
      <div ref={listRef} role="tablist" aria-label={label} className={styles.list} onKeyDown={onKeyDown}>
        {options.map((o) => {
          const selected = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              role="tab"
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              className={cx(styles.tab, selected && styles.selected)}
              onClick={() => onChange(o.value)}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      {aside && <div className={styles.aside}>{aside}</div>}
    </div>
  );
}
